import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { Truck } from "lucide-react";
import { createLoad, CreateLoadParams } from "@/api/vendor/createLoad";
import { toast } from "sonner";

// Import components and data
import { LocationCard } from "./LocationCard";
import { WeightCard } from "./WeightCard";
import { ContentsCard } from "./ContentsCard";
import { DateTimeCard } from "./DateTimeCard";
import { formSchema, FormData } from "./schema";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getStations } from "@/api/receiver/getStations";
import { BolCard } from "./BolCard";

interface CreateLoadModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

export function AddLoadModal({ isOpen, setIsOpen }: CreateLoadModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      selectedWarehouse: "",
      selectedDestination: "",
      weight: 0,
      contents: "",
      pickupStartDate: null,
      pickupEndDate: null,
      pickupStartTime: null,
      pickupEndTime: null,
      dropOffStartDate: null,
      dropOffEndDate: null,
      dropOffStartTime: null,
      dropOffEndTime: null,
      bol: null,
    },
  });

  const { data: stations } = useQuery({
    queryKey: ["stations"],
    queryFn: () => getStations(),
  });

  const queryClient = useQueryClient();

  const selectedWarehouse = form.watch("selectedWarehouse");
  const selectedDestination = form.watch("selectedDestination");

  // Find selected data from the actual stations data
  const selectedWarehouseData = stations?.vendorStations?.find(
    (station) => station.id === selectedWarehouse
  );
  const selectedDestinationData = stations?.receiverStations?.find(
    (station) => station.id === selectedDestination
  );

  const createDateTime = (date: Date | null, time: string | null) => {
    if (!date || !time) return null;
    const result = new Date(date);
    const [hours, minutes] = time.split(":");
    result.setHours(parseInt(hours), parseInt(minutes));
    return result;
  };

  const handleSubmit = async (data: FormData) => {
    setIsSubmitting(true);

    try {
      const loadParams: CreateLoadParams = {
        sourceStationId: data.selectedWarehouse,
        destinationStationId: data.selectedDestination,
        pickupStart: createDateTime(data.pickupStartDate, data.pickupStartTime),
        pickupEnd: createDateTime(data.pickupStartDate, data.pickupEndTime),
        deliveryStart: createDateTime(
          data.dropOffStartDate,
          data.dropOffStartTime
        ),
        deliveryEnd: createDateTime(data.dropOffStartDate, data.dropOffEndTime),
        weight: data.weight,
        notes: data.contents,
        pieces: Math.ceil(data.weight / 100),
        bol: data.bol || "",
      };

      console.log("Creating load with params:", loadParams);
      await createLoad(loadParams);

      toast.success("Load Created Successfully", {
        description: "Your freight load has been scheduled",
      });
      queryClient.invalidateQueries({ queryKey: ["loads", "receiver"] });

      form.reset();
      setIsOpen(false);
    } catch (error) {
      console.error("Error creating load:", error);
      toast.error("Failed to Create Load", {
        description:
          "Please try again or contact support if the problem persists",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      form.reset();
      setIsOpen(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto rounded-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Truck className="h-6 w-6 text-blue-600" />
            Create New Load
          </DialogTitle>
          <DialogDescription>
            Schedule a new freight pickup from one of your warehouse locations
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-6">
                <LocationCard
                  control={form.control}
                  fieldName="selectedWarehouse"
                  title="Pickup Location"
                  description="Select the warehouse for freight pickup"
                  options={stations?.vendorStations || []}
                  selectedData={selectedWarehouseData}
                  colorScheme="blue"
                />

                <LocationCard
                  control={form.control}
                  fieldName="selectedDestination"
                  title="Drop off Location"
                  description="Select the destination warehouse"
                  options={stations?.receiverStations || []}
                  selectedData={selectedDestinationData}
                  colorScheme="green"
                />

                <WeightCard control={form.control} />
                <BolCard control={form.control} />
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                <ContentsCard control={form.control} />

                <DateTimeCard
                  control={form.control}
                  title="Pickup Window"
                  description="When should the freight be picked up? (Optional)"
                  startDateField="pickupStartDate"
                  startTimeField="pickupStartTime"
                  endTimeField="pickupEndTime"
                  getValues={form.getValues}
                />

                <DateTimeCard
                  control={form.control}
                  title="Drop off Window"
                  description="When should the freight be dropped off? (Optional)"
                  startDateField="dropOffStartDate"
                  startTimeField="dropOffStartTime"
                  endTimeField="dropOffEndTime"
                  getValues={form.getValues}
                />
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end gap-3 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" className="px-8" disabled={isSubmitting}>
                {isSubmitting ? "Creating..." : "Create Load"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
